<template>
  <div
    class="accruals-estimate-gl-assign"
    :data-testid="testid"
  >
    <div class="accruals-estimate-gl-assign__step">
      <div class="accruals-estimate-gl-assign__copy">
        <span class="accruals-estimate-gl-assign__badge" aria-hidden="true">
          <img
            :src="assets.ESTIMATES_STEP_CHECK"
            alt=""
            class="accruals-estimate-gl-assign__check"
          />
        </span>
        <span class="accruals-estimate-gl-assign__confirmed">
          {{ copy.CONFIRMED_PREFIX }} · {{ confirmedAmountLabel }}
        </span>
        <span class="accruals-estimate-gl-assign__arrow" aria-hidden="true">→</span>
        <span class="accruals-estimate-gl-assign__next">{{ copy.NEXT_STEP }}</span>
      </div>
      <base-button
        primary
        small
        class="shadow-off primaryBtn accruals-estimate-gl-assign__save"
        :class="{ 'accruals-estimate-gl-assign__save--muted': !canSave }"
        :loading="saving"
        :disabled="!canReview || saving"
        :data-testid="`${testid}__record-btn`"
        @click="onRecordEntry"
      >
        {{ copy.RECORD_ENTRY }}
      </base-button>
    </div>

    <div class="accruals-estimate-gl-assign__body">
      <div class="accruals-estimate-gl-assign__table">
        <div class="accruals-estimate-gl-assign__head">
          <span>{{ copy.REFERENCE }}</span>
          <span class="accruals-estimate-gl-assign__status-heading">{{ copy.STATUS }}</span>
          <span class="accruals-estimate-gl-assign__amount-heading">{{ copy.ESTIMATED_AMOUNT }}</span>
          <span>{{ copy.DEBIT_GL }}</span>
          <span>{{ copy.CREDIT_GL }}</span>
          <span></span>
        </div>
        <div
          v-for="(line, index) in lines"
          :key="lineKey(line) || `gl-${index}`"
          class="accruals-estimate-gl-assign__row"
          :data-testid="`${testid}__row--${index}`"
        >
          <span class="accruals-estimate-gl-assign__ref">
            <span
              class="accruals-estimate-gl-assign__tag"
              :class="{
                'accruals-estimate-gl-assign__tag--vendor': isVendorRef(line),
              }"
            >
              {{ refLabel(line) }}
            </span>
          </span>
          <span
            class="accruals-estimate-gl-assign__status"
            :data-testid="`${testid}__status--${index}`"
          >
            <StatusIndicator
              v-if="lineStatus(line)"
              :variant="statusVariant(lineStatus(line))"
              :custom-label="statusLabel(lineStatus(line))"
              size="sm"
              outlined
              :data-testid="`${testid}__status-indicator--${index}`"
            />
          </span>
          <span class="accruals-estimate-gl-assign__amount">
            {{ formatMoney(lineAmount(line)) }}
          </span>
          <span class="accruals-estimate-gl-assign__select">
            <lb-dropdown
              :value="rowDebitGl(line)"
              :items="glDropdownItems"
              :hidedetails="!debitError(line)"
              :error="debitError(line)"
              label=""
              searchable
              :placeholder="copy.PLACEHOLDER"
              class="accruals-estimate-gl-assign__input"
              :class="{ 'accruals-estimate-gl-assign__input--error': Boolean(debitError(line)) }"
              :data-testid="`${testid}__debit-gl--${index}`"
              @input="setRowDebitGl(line, $event)"
            />
          </span>
          <span class="accruals-estimate-gl-assign__select">
            <lb-dropdown
              :value="rowCreditGl(line)"
              :items="glDropdownItems"
              :hidedetails="!creditError(line)"
              :error="creditError(line)"
              label=""
              searchable
              :placeholder="copy.PLACEHOLDER"
              class="accruals-estimate-gl-assign__input"
              :class="{ 'accruals-estimate-gl-assign__input--error': Boolean(creditError(line)) }"
              :data-testid="`${testid}__credit-gl--${index}`"
              @input="setRowCreditGl(line, $event)"
            />
          </span>
          <button
            type="button"
            class="accruals-estimate-gl-assign__split"
            aria-label="Open split view"
            :data-testid="`${testid}__split--${index}`"
            @click="$emit('open-split-view', line)"
          >
            <img
              :src="assets.ESTIMATES_SPLIT_VIEW"
              alt=""
              class="accruals-estimate-gl-assign__split-img"
            />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StatusIndicator from '@/components/StatusIndicator.vue';
import { STATUS_VARIANTS } from '@/components/utils/status-constants';
import { ACCRUALS_ASSETS } from '../accrualsPageConstants';
import {
  ACCRUALS_ESTIMATE_STATUS_CONFIG,
  ACCRUALS_GL_ASSIGNMENT_COPY,
  estimateLineRecordAmount,
  formatEstimateMoney,
  formatGlOptionLabel,
  isVendorLevelEstimateRow,
  vendorReferenceLabel,
  workbenchColumnStatusKey,
} from '../accrualsEstimatesConstants';

export default {
  name: 'AccrualsEstimateGlAssign',
  components: {
    StatusIndicator,
  },
  props: {
    group: {
      type: Object,
      default: () => ({}),
    },
    lines: {
      type: Array,
      default: () => [],
    },
    debitGlById: {
      type: Object,
      default: () => ({}),
    },
    creditGlById: {
      type: Object,
      default: () => ({}),
    },
    canReview: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    testid: {
      type: String,
      default: 'accruals-estimate-gl-assign',
    },
  },
  data() {
    return {
      assets: ACCRUALS_ASSETS,
      copy: ACCRUALS_GL_ASSIGNMENT_COPY,
      showFieldErrors: false,
    };
  },
  computed: {
    storeGlOptions() {
      return this.$store.state.accruals?.glOptions
        || this.$store.state.m_financialclose?.accruals?.glOptions
        || [];
    },
    glDropdownItems() {
      const options = this.storeGlOptions.map((row) => formatGlOptionLabel(row));
      (this.lines || []).forEach((line) => {
        [this.rowDebitGl(line), this.rowCreditGl(line)].forEach((value) => {
          const label = formatGlOptionLabel(value);
          if (label && !options.includes(label)) options.push(label);
        });
      });
      return options;
    },
    confirmedAmountLabel() {
      const total = (this.lines || []).reduce((sum, line) => (
        sum + (Number(this.lineAmount(line)) || 0)
      ), 0);
      return formatEstimateMoney(total);
    },
    canSave() {
      if (!this.canReview || !(this.lines || []).length) return false;
      return this.lines.every((line) => this.rowIsComplete(line));
    },
  },
  created() {
    this.$store.dispatch('accruals/loadGlOptions');
  },
  beforeDestroy() {
    this.showFieldErrors = false;
  },
  methods: {
    formatMoney(value) {
      return formatEstimateMoney(value);
    },
    lineKey(line) {
      return String(line?._id || line?.lineKey || line?.poNumber || '');
    },
    lineAmount(line) {
      return estimateLineRecordAmount(line);
    },
    isVendorRef(line) {
      return isVendorLevelEstimateRow(line) && !line?.poNumber;
    },
    refLabel(line) {
      return vendorReferenceLabel(this.group, line);
    },
    lineStatus(line) {
      return workbenchColumnStatusKey(line);
    },
    statusVariant(status) {
      return ACCRUALS_ESTIMATE_STATUS_CONFIG[status]?.variant || STATUS_VARIANTS.UNKNOWN;
    },
    statusLabel(status) {
      return ACCRUALS_ESTIMATE_STATUS_CONFIG[status]?.label || status || '—';
    },
    rowDebitGl(line) {
      const key = this.lineKey(line);
      if (this.debitGlById[key] != null && this.debitGlById[key] !== '') {
        return this.debitGlById[key];
      }
      return line?.debitGl || '';
    },
    rowCreditGl(line) {
      const key = this.lineKey(line);
      if (this.creditGlById[key] != null && this.creditGlById[key] !== '') {
        return this.creditGlById[key];
      }
      return line?.creditGl || '';
    },
    rowIsComplete(line) {
      return Boolean(this.rowDebitGl(line)) && Boolean(this.rowCreditGl(line));
    },
    debitError(line) {
      if (!this.showFieldErrors || this.rowDebitGl(line)) return '';
      return this.copy.FIELD_REQUIRED;
    },
    creditError(line) {
      if (!this.showFieldErrors || this.rowCreditGl(line)) return '';
      return this.copy.FIELD_REQUIRED;
    },
    setRowDebitGl(line, value) {
      this.$emit('update:debitGl', { line, value });
    },
    setRowCreditGl(line, value) {
      this.$emit('update:creditGl', { line, value });
    },
    onRecordEntry() {
      if (!this.canReview || this.saving) return;
      if (!this.canSave) {
        this.showFieldErrors = true;
        this.$store.commit('sbError', this.copy.RECORD_ENTRY_INVALID, { root: true });
        console.log('[AccrualsEstimateGlAssign]:', {
          action: 'record-entry-invalid',
          groupId: this.group?._id,
        });
        return;
      }
      this.showFieldErrors = false;
      const items = this.lines.map((line) => ({
        row: line,
        debitGl: this.rowDebitGl(line),
        creditGl: this.rowCreditGl(line),
      }));
      console.log('[AccrualsEstimateGlAssign]:', {
        action: 'record-entry',
        count: items.length,
        groupId: this.group?._id,
      });
      this.$emit('save', { group: this.group, items });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsEstimateGlAssign.scss';
</style>
